import { imageBaseUrl, imageDefinition as imageDefinitions } from '../imageDefinition';
import type { Fabric, OptionsSelection, typeOptionValue } from '../interfaces';
import { singleImage } from './transformation';
import { ConfigurationTypes } from '../productDefinition';
import { LayerDefinition } from '../preview/layers';

interface ImageDefinition {
  sizes: string[];
  srcSetWidths: number[];
  ratio: number;
}

const createSourcesets = (
  srcSetWidths: number[],
  sizeUrls: string[],
  ratio: number,
  noWidth: boolean,
  noTransformation?: boolean,
) => {
  return !noTransformation
    ? srcSetWidths
        .map(width => {
          const sizeUrl = sizeUrls.find(url =>
            noWidth ? url.includes(`${width * ratio}`) : url.includes(`${width}`) && url.includes(`${width * ratio}`),
          );
          return `${sizeUrl} ${width}w`;
        })
        .join(',')
    : '';
};

export const imageUrlBuilder = ({
  imageDefinition,
  imageUrls,
  noTransformation,
}: {
  imageDefinition: ImageDefinition;
  imageUrls: string[];
  noTransformation?: boolean;
}) => {
  const { srcSetWidths, ratio, sizes } = imageDefinition;

  const source = {
    src: '',
    srcSheet: '',
    srcRotation: '',
    srcset: '',
    srcsetRotation: '',
    sizes: sizes.join(','),
    ratio,
  };

  let transformations: string[] = [];
  let transformationSheet: string = '';
  let [fileName] = imageUrls;

  transformations = singleImage({ srcSetWidths, ratio });
  transformationSheet = singleImage({ srcSetWidths: [imageDefinitions.sheetWidth], ratio, noWidth: true })[0];

  const sizeUrls = transformations.map(
    transformation => `${imageBaseUrl}/${noTransformation ? 'f_auto/' : transformation + '/'}${fileName}`,
  );

  const lastIndex = fileName.lastIndexOf('/');
  const spriteFileName = fileName.slice(0, lastIndex) + '/rotation' + fileName.slice(lastIndex);
  const sizeUrlsSheet = `${imageBaseUrl}/${noTransformation ? 'f_auto/' : transformationSheet + '/'}${spriteFileName}_sheet`;

  const sizeUrlsRotation = transformations.map(
    transformation => `${imageBaseUrl}/${noTransformation ? 'f_auto/' : transformation + '/'}${fileName}_{rotation}`,
  );

  const index = Math.floor((srcSetWidths.length - 1) / 2);
  source.src = sizeUrls[index];
  source.srcSheet = sizeUrlsSheet;
  source.srcRotation = sizeUrlsRotation[index];
  source.srcset = createSourcesets(srcSetWidths, sizeUrls, ratio, false, noTransformation);
  source.srcsetRotation = createSourcesets(srcSetWidths, sizeUrlsRotation, ratio, true, noTransformation);

  return source;
};

export const createLayerUrlFromTemplate = (
  stringWithTemplate: string,
  optionValues: typeOptionValue,
  optionsSelection: OptionsSelection,
  selectedFabric: Fabric,
  configurationTypeId: ConfigurationTypes,
) => {
  const placeholders = stringWithTemplate?.match(/\{(.*?)\}/g);
  let newString = `${stringWithTemplate?.trim()}`;
  if (placeholders) {
    placeholders.forEach(ph => {
      const key = ph.slice(1, ph.length - 1);

      const selectedValue =
        key === 'fabricId'
          ? selectedFabric?.layerImageName
          : (optionValues[optionsSelection[configurationTypeId][+key]]?.layerImageName ??
            optionValues[optionsSelection[configurationTypeId][+key]]?.imageName);

      newString = newString.replace(ph, `${selectedValue}`).trim();
    });
  }

  return newString;
};

export const createPreviewData = async (
  processedImageTemplates: string[],
  fallbackUrl: string,
  filterNonExistingLayers: boolean = false,
) => {
  const fallbackImage = imageUrlBuilder({
    imageUrls: [fallbackUrl],
    imageDefinition: imageDefinitions.layer,
  });

  let layers: LayerDefinition[] = [];

  processedImageTemplates.forEach(tmp => {
    layers.push(
      imageUrlBuilder({
        imageUrls: [tmp],
        imageDefinition: imageDefinitions.layer,
      }),
    );
  });

  if (filterNonExistingLayers) {
    layers = await filterOutNonExistingLayers(layers[0], layers.slice(1), fallbackImage);
  }

  return {
    layers: layers,
    fallbackImage,
  };
};

export const createConfigurationImageLayers = (processedImageTemplates: string[]) => {
  return processedImageTemplates.map(tmp => {
    return imageUrlBuilder({
      imageUrls: [tmp],
      imageDefinition: imageDefinitions.layer,
    }).src;
  });
};

export const checkIfLayerExist = async (layer: LayerDefinition) => {
  try {
    const response = await fetch(`${layer.src}`);
    return response.ok;
  } catch (error) {
    return false;
  }
};

export const filterOutNonExistingLayers = async (
  baseLayer: LayerDefinition,
  otherLayers: LayerDefinition[],
  fallbackLayer: LayerDefinition,
) => {
  const baseLayerExist = await checkIfLayerExist(baseLayer);

  if (!baseLayerExist) {
    return [fallbackLayer];
  }

  let otherExistingLayers: { [key: string]: LayerDefinition } = {};

  await Promise.allSettled(
    otherLayers.map(async (layer, index) => {
      const exist = await checkIfLayerExist(layer);
      if (exist) otherExistingLayers[index] = layer;
    }),
  );

  return [baseLayer, ...Object.values(otherExistingLayers)];
};
